// Simple file system structure for the web terminal
// This serves as a simulated file system that can be loaded from a directory

class FileSystem {
    constructor() {
        this.root = {
            type: 'directory',
            name: '/',
            children: {},
            parent: null
        };
        this.currentPath = '/';
        this.currentDir = this.root;
    }

    // Initialize file system from a JSON structure
    loadFromJSON(jsonStructure) {
        this.root = jsonStructure;
        this.currentPath = '/';
        this.currentDir = this.root;
        return this;
    }

    // Get current working directory
    pwd() {
        return this.currentPath;
    }

    // Change directory
    cd(path) {
        if (!path || path === '.' || path === './') {
            return true; // Stay in the current directory
        }

        if (path === '..') {
            if (this.currentDir.parent) {
                this.currentDir = this.currentDir.parent;
                this.currentPath = this._getPathFromNode(this.currentDir);
                return true;
            } else {
                return false; // Already at root
            }
        }

        if (path === '/') {
            this.currentDir = this.root;
            this.currentPath = '/';
            return true;
        }

        // Handle path with multiple segments
        if (path.includes('/')) {
            const isAbsolute = path.startsWith('/');
            const segments = path.split('/').filter(seg => seg.length > 0);

            let current = isAbsolute ? this.root : this.currentDir;
            let currentPath = isAbsolute ? '/' : this.currentPath;

            for (const segment of segments) {
                if (segment === '..') {
                    if (current.parent) {
                        current = current.parent;
                        currentPath = this._getPathFromNode(current);
                    }
                    continue;
                }

                if (segment === '.') {
                    continue;
                }

                if (current.children && current.children[segment] &&
                    current.children[segment].type === 'directory') {
                    current = current.children[segment];
                    currentPath = this._getPathFromNode(current);
                } else {
                    return false; // Directory not found
                }
            }

            this.currentDir = current;
            this.currentPath = currentPath;
            return true;
        }

        // Handle simple case - single directory name
        if (this.currentDir.children &&
            this.currentDir.children[path] &&
            this.currentDir.children[path].type === 'directory') {
            this.currentDir = this.currentDir.children[path];
            this.currentPath = this._getPathFromNode(this.currentDir);
            return true;
        }

        return false; // Directory not found
    }

    // List files in current directory
    ls(path = null) {
        const dir = path ? this.findNode(path) : this.currentDir;

        if (!dir || dir.type !== 'directory') {
            return null;
        }

        const result = {
            directories: [],
            files: []
        };

        for (const name in dir.children) {
            const item = dir.children[name];
            if (item.type === 'directory') {
                result.directories.push(name);
            } else {
                result.files.push(name);
            }
        }

        result.directories.sort();
        result.files.sort();

        return result;
    }

    // Find a node (file or directory) by path
    findNode(path) {
        if (!path) return null;

        if (path === '/') return this.root;
        if (path === '.') return this.currentDir;
        if (path === '..') return this.currentDir.parent || this.root;

        const isAbsolute = path.startsWith('/');
        const segments = path.split('/').filter(seg => seg.length > 0);

        let current = isAbsolute ? this.root : this.currentDir;

        for (const segment of segments) {
            if (segment === '.') continue;
            if (segment === '..') {
                current = current.parent || this.root;
                continue;
            }

            if (!current.children || !current.children[segment]) {
                return null; // Node not found
            }

            current = current.children[segment];
        }

        return current;
    }

    // Get file content
    cat(path) {
        const node = this.findNode(path);

        if (!node) {
            return null; // File not found
        }

        if (node.type === 'directory') {
            return { error: true, message: `${path}: Is a directory` };
        }

        return node.content || '';
    }

    // Helper method to get path from node
    _getPathFromNode(node) {
        if (node === this.root) return '/';

        const segments = [];
        let current = node;

        while (current !== this.root) {
            segments.unshift(current.name);
            current = current.parent;
        }

        return '/' + segments.join('/');
    }
}

export default FileSystem;